import { Extension } from '@tiptap/core';
import { Mapping } from '@tiptap/pm/transform';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { timeRanges } from './timeMark';

/** Eine Zeitmarke, deren Text ganz gelöscht wurde: ihr Text vorher */
export interface LostTime {
  text: string;
}

interface GuardState {
  /**
   * Zeitmarken, in deren Text der Nutzer getippt hat (in dieser Sitzung), durch spätere Änderungen mitgeführt. Sagt der Text danach
   * etwas anderes als die Marke bzw. nichts Erkennbares mehr, ist das ein Widerspruch an der Stelle (timeConflicts.ts).
   */
  edited: { from: number; to: number }[];
}

/**
 * Meta für den Wächter: `{ clear: [from, to] }` vergisst geänderte Stellen in diesem Bereich, `{ add }` merkt welche (der Wächter selbst);
 * jede Meta (auch `true`) sagt: Diese Änderung ist nicht getippt (etwa ein angenommener Vorschlag).
 */
export type GuardMeta = { clear: [number, number] } | { add: { from: number; to: number }[] } | true;

export const timeGuardKey = new PluginKey<GuardState>('timeMarkGuard');

/** vom Verlauf (Rückgängig/Wiederholen) oder von außen gesetzter Inhalt bzw. von Dispositio selbst: kein Bearbeiten durch den Nutzer */
const notByUser = (tr: Transaction): boolean => !!tr.getMeta('preventUpdate') || tr.getMeta('history$') !== undefined || tr.getMeta(timeGuardKey) !== undefined;

/** Geänderte Stellen, die eine Zeitmarke berühren (für die Darstellung als Widerspruch) */
export const editedTimes = (state: Parameters<typeof timeGuardKey.getState>[0]): { from: number; to: number }[] => timeGuardKey.getState(state)?.edited ?? [];

/**
 * Zeitmarken folgen ihrem Text, ändern ihn aber nie (PLAN.md §16, §16d): Tippt der Nutzer im Text einer Zeitmarke, bleibt die Marke
 * stehen und die Stelle gilt als geändert – passt der Text danach nicht mehr zur Zeit, zeigt der Editor das rot an der Stelle. Löscht
 * er den Text ganz, ist mit ihm die Marke weg; `onLost` meldet es (damit der Nutzer eine neue Zeit setzen kann). Rückgängig stellt
 * beides wieder her.
 */
export const timeMarkGuard = (onLost: (lost: LostTime[]) => void): Extension =>
  Extension.create({
    name: 'timeMarkGuard',
    // vor den übrigen Plugins: die Widersprüche (timeConflicts.ts) lesen seine geänderten Stellen beim selben Schritt
    priority: 101,
    addProseMirrorPlugins() {
      return [
        new Plugin<GuardState>({
          key: timeGuardKey,
          state: {
            init: () => ({ edited: [] }),
            apply: (tr, value, _old, state) => {
              const meta = tr.getMeta(timeGuardKey) as GuardMeta | undefined;
              if (!tr.docChanged && (meta === undefined || meta === true)) return value;
              let edited = !tr.docChanged
                ? value.edited
                : value.edited.flatMap((r) => {
                    const from = tr.mapping.map(r.from, 1);
                    const to = tr.mapping.map(r.to, -1);
                    return from < to ? [{ from, to }] : [];
                  });
              if (meta && meta !== true && 'add' in meta) edited = [...edited, ...meta.add];
              if (meta && meta !== true && 'clear' in meta) edited = edited.filter((r) => r.to <= meta.clear[0] || r.from >= meta.clear[1]);
              if (edited.length === 0) return value.edited.length === 0 ? value : { edited };
              // nur Stellen, die noch eine Zeitmarke tragen
              const marks = timeRanges(state.doc);
              return { edited: edited.filter((r) => marks.some((t) => t.from < r.to && t.to > r.from)) };
            },
          },
          appendTransaction: (trs, oldState, newState) => {
            if (!trs.some((t) => t.docChanged) || trs.some(notByUser)) return null;
            const mapping = new Mapping();
            for (const t of trs) mapping.appendMapping(t.mapping);
            const changed: { from: number; to: number }[] = [];
            const lost: LostTime[] = [];
            for (const r of timeRanges(oldState.doc)) {
              const before = oldState.doc.textBetween(r.from, r.to, ' ', '￼');
              // Einfügen direkt am Rand gehört nicht dazu (die Marke wächst nicht mit)
              const from = mapping.map(r.from, 1);
              const to = mapping.map(r.to, -1);
              const after = from < to ? newState.doc.textBetween(from, to, ' ', '￼') : '';
              if (after === before) continue;
              if (after.trim() === '') lost.push({ text: before });
              else changed.push({ from, to });
            }
            if (lost.length > 0) queueMicrotask(() => onLost(lost));
            if (changed.length === 0) return null;
            return newState.tr.setMeta(timeGuardKey, { add: changed }).setMeta('addToHistory', false);
          },
        }),
      ];
    },
  });
